/**
 * Puzzle Upload Component — JavaScript
 *
 * Single-image upload for puzzle photo magnets.
 * Auto-crops image by variant grid ratio (e.g. 3x3→1:1, 4x3→4:3)
 * and displays a grid overlay showing how magnets will be cut.
 *
 * @version 1.0.0
 * @compatible Horizon Theme (Shopify Online Store 2.0)
 */

class PuzzleUploadComponent extends HTMLElement {
  constructor() {
    super();

    // State
    this.file = null; // { file: File, dataUrl: string, originalDataUrl: string, id: string }
    this.gridCols = 3;
    this.gridRows = 3;
    this.cropRatio = 1; // cols / rows
    this.rotation = 0; // cumulative rotation: 0, 90, 180, 270
    this.baseGridCols = 3; // original variant cols (before rotation swap)
    this.baseGridRows = 3;
    this.maxFileSizeMB = 10;
    this.allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    this.isSubmitting = false;

    // DOM references
    this.modal = null;
    this.trigger = null;
    this.fileInput = null;
    this.dropzone = null;
    this.imageContainer = null;

    // Bind methods
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleChangePicture = this.handleChangePicture.bind(this);
    this.handleRotate = this.handleRotate.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.render();
    this.bindEvents();
    this.detectGridVariant();

    // Listen for variant changes
    this._boundVariantUpdate = this.handleVariantUpdate.bind(this);
    this._boundVariantClick = this.handleVariantClick.bind(this);
    document.addEventListener('variant:update', this._boundVariantUpdate);
    document.addEventListener('variant:selected', this._boundVariantClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('variant:update', this._boundVariantUpdate);
    document.removeEventListener('variant:selected', this._boundVariantClick);
    if (this.productForm && this._formSubmitHandler) {
      this.productForm.removeEventListener('submit', this._formSubmitHandler, { capture: true });
    }
  }

  // ========================================
  // RENDER
  // ========================================

  render() {
    const buttonText = this.dataset.buttonText || 'Upload your photo';

    this.innerHTML = `
      <button type="button" class="puzzle-upload-trigger" data-testid="puzzle-upload-trigger">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <span>${buttonText}</span>
      </button>

      <!-- Modal -->
      <div class="puzzle-upload-modal" data-testid="puzzle-upload-modal" role="dialog" aria-modal="true" aria-labelledby="puzzle-upload-title">
        <div class="puzzle-upload-modal__container">
          <header class="puzzle-upload-modal__header">
            <h2 class="puzzle-upload-modal__title" id="puzzle-upload-title">Upload your puzzle photo</h2>
            <button type="button" class="puzzle-upload-modal__close" aria-label="Close">&times;</button>
          </header>

          <div class="puzzle-upload-modal__body">
            <!-- Step 1: Upload -->
            <div class="puzzle-upload-step puzzle-upload-step--upload" data-step="upload">
              <div class="puzzle-upload-dropzone" data-testid="puzzle-upload-dropzone">
                <svg class="puzzle-upload-dropzone__icon" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="8" y="12" width="36" height="36" rx="4"/>
                  <rect x="20" y="16" width="36" height="36" rx="4"/>
                  <circle cx="20" cy="28" r="4"/>
                  <path d="M8 40 l12 -12 l8 8 l12 -12 l4 4"/>
                </svg>
                <p class="puzzle-upload-dropzone__text">Drag & Drop your photo here</p>
                <button type="button" class="puzzle-upload-pc-btn" data-testid="puzzle-upload-pc-btn">
                  <span class="puzzle-upload-text-desktop">+ Upload From PC</span>
                  <span class="puzzle-upload-text-mobile">+ Upload From Gallery</span>
                </button>
              </div>

              <div class="puzzle-upload-sources">
                <p class="puzzle-upload-sources__label">or</p>
                <div class="puzzle-upload-sources__icons">
                  <button type="button" class="puzzle-upload-source-icon" data-action="camera" title="Camera">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </button>
                </div>
              </div>

              <input
                type="file"
                class="puzzle-upload-input"
                accept="image/jpeg,image/png,image/webp,image/gif"
                data-testid="puzzle-upload-input"
              />
              <input
                type="file"
                class="puzzle-upload-camera-input"
                accept="image/*"
                capture="environment"
                style="display: none;"
              />
            </div>

            <!-- Step 2: Preview -->
            <div class="puzzle-upload-step puzzle-upload-step--preview" data-step="preview" style="display: none;">
              <div class="puzzle-upload-preview">
                <div class="puzzle-upload-preview__grid-wrapper">
                  <!-- Image + Grid Overlay -->
                  <div class="puzzle-upload-preview__image-container" data-testid="puzzle-image-container">
                    <!-- Image and grid overlay rendered by JS -->
                  </div>

                  <!-- Sidebar -->
                  <div class="puzzle-upload-preview__sidebar">
                    <div class="puzzle-upload-preview__info">
                      <h3>Product Preview</h3>
                      <div class="puzzle-upload-preview__note">
                        <span class="puzzle-upload-preview__note-icon"></span>
                        <span>The grid shows how your photo will be split. Each cell represents one photo magnet you will receive.</span>
                      </div>
                      <p class="puzzle-upload-preview__tip">You will receive:</p>
                      <p class="puzzle-upload-preview__variant-label" data-variant-label>
                        <strong data-grid-label>3×3</strong> = <span data-magnet-count>9</span> photo magnets
                      </p>
                    </div>

                    <div class="puzzle-upload-sidebar-buttons">
                      <div class="puzzle-upload-preview__actions">
                        <button type="button" class="puzzle-upload-action-btn puzzle-upload-action-btn--change" data-action="change-picture" title="Change picture">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                          <span>Change picture</span>
                        </button>
                        <button type="button" class="puzzle-upload-action-btn puzzle-upload-action-btn--resize" data-action="resize" title="Resize/crop photo">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                            <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/>
                            <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/>
                          </svg>
                          <span>Resize</span>
                        </button>
                        <button type="button" class="puzzle-upload-action-btn puzzle-upload-action-btn--rotate" data-action="rotate" title="Rotate 90°">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                            <path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                          </svg>
                          <span>Rotate</span>
                        </button>
                      </div>
                      <button type="button" class="puzzle-upload-addtocart-btn" data-testid="puzzle-upload-addtocart">
                        ADD TO CART
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Hidden inputs for form submission -->
      <div class="puzzle-upload-hidden-inputs" style="display: none;" data-testid="puzzle-upload-hidden-inputs">
      </div>
    `;

    // Cache DOM references
    this.trigger = this.querySelector('.puzzle-upload-trigger');
    this.modal = this.querySelector('.puzzle-upload-modal');
    this.fileInput = this.querySelector('.puzzle-upload-input');
    this.dropzone = this.querySelector('.puzzle-upload-dropzone');
    this.imageContainer = this.querySelector('.puzzle-upload-preview__image-container');
    this.uploadStep = this.querySelector('[data-step="upload"]');
    this.previewStep = this.querySelector('[data-step="preview"]');
  }

  // ========================================
  // EVENT BINDING
  // ========================================

  bindEvents() {
    this.trigger?.addEventListener('click', this.handleTriggerClick);
    this.fileInput?.addEventListener('change', this.handleFileSelect);

    // PC Upload Button
    this.modal.querySelector('.puzzle-upload-pc-btn')?.addEventListener('click', () => {
      this.fileInput?.click();
    });

    // Camera Input
    const cameraInput = this.modal.querySelector('.puzzle-upload-camera-input');
    this.modal.querySelector('[data-action="camera"]')?.addEventListener('click', () => {
      cameraInput?.click();
    });
    cameraInput?.addEventListener('change', (e) => {
      this.handleFileSelect(e);
      e.target.value = '';
    });

    // Drag & Drop
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropzone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
    this.dropzone.addEventListener('dragover', this.handleDragOver);
    this.dropzone.addEventListener('dragleave', this.handleDragLeave);
    this.dropzone.addEventListener('drop', this.handleDrop);

    // Modal Close
    this.querySelector('.puzzle-upload-modal__close')?.addEventListener('click', this.handleModalClose);
    this.modal?.addEventListener('click', (e) => {
      if (e.target === this.modal) this.handleModalClose();
    });
    document.addEventListener('keydown', this.handleKeyDown);

    // Action buttons
    this.modal.querySelector('[data-action="change-picture"]')?.addEventListener('click', this.handleChangePicture);
    this.modal.querySelector('[data-action="resize"]')?.addEventListener('click', () => this.openResizeModal());
    this.modal.querySelector('[data-action="rotate"]')?.addEventListener('click', this.handleRotate);
    this.modal.querySelector('.puzzle-upload-addtocart-btn')?.addEventListener('click', this.handleAddToCart);

    // Form intercept
    this.interceptFormSubmit();
  }

  interceptFormSubmit() {
    const productFormComponent = document.querySelector('product-form-component');
    const productForm = productFormComponent?.querySelector('form') ||
      document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;
    this.productForm = productForm;

    this._formSubmitHandler = (e) => {
      if (this.file) this.syncInputsToProductForm();
    };
    productForm.addEventListener('submit', this._formSubmitHandler, { capture: true });
  }

  // ========================================
  // VARIANT DETECTION
  // ========================================

  detectGridVariant() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) {
      this.parseGridFromString(this.dataset.defaultGrid || '3x3');
      return;
    }

    const fieldsets = variantPicker.querySelectorAll('fieldset');
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend, .variant-picker__option-name');
      const legendText = legend?.textContent?.trim().toLowerCase() || '';
      if (!legendText.includes('size') && !legendText.includes('grid') && !legendText.includes('package')) continue;

      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput) {
        const label = fieldset.querySelector(`label[for="${checkedInput.id}"]`);
        const value = label?.textContent?.trim() || checkedInput.value;
        this.parseGridFromString(value);
      }
      break;
    }

    this.syncVariantId();
    this.updateVariantDisplay();
  }

  parseGridFromString(value) {
    if (!value) return;
    const match = value.match(/(\d+)\s*[x×X]\s*(\d+)/);
    if (match) {
      const oldCols = this.gridCols;
      const oldRows = this.gridRows;
      const newCols = parseInt(match[1], 10);
      const newRows = parseInt(match[2], 10);

      // Store base grid and reset rotation on variant change
      this.baseGridCols = newCols;
      this.baseGridRows = newRows;
      this.rotation = 0;

      this.gridCols = newCols;
      this.gridRows = newRows;
      this.cropRatio = this.gridCols / this.gridRows;

      if (this.file && (oldCols !== this.gridCols || oldRows !== this.gridRows)) {
        this.reCropForNewRatio();
      }
    }
  }

  handleVariantUpdate() {
    setTimeout(() => {
      this.detectGridVariant();
      this.updatePreview();
    }, 100);
  }

  handleVariantClick() {
    setTimeout(() => {
      this.detectGridVariant();
      this.updatePreview();
    }, 50);
  }

  syncVariantId() {
    const pickerVariant = this.getVariantFromPicker();
    if (pickerVariant) {
      this.dataset.variantId = pickerVariant;
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant) {
      this.dataset.variantId = urlVariant;
    }
  }

  getVariantFromPicker() {
    const section = this.closest('[data-section-id]') || this.closest('.shopify-section');
    const scope = section || document;
    const variantPicker = scope.querySelector('variant-picker');
    if (!variantPicker) return null;
    const checkedInputs = variantPicker.querySelectorAll('input:checked[data-variant-id]');
    if (checkedInputs.length > 0) {
      return checkedInputs[checkedInputs.length - 1].dataset.variantId;
    }
    return null;
  }

  updateVariantDisplay() {
    const gridLabel = this.querySelector('[data-grid-label]');
    const magnetCount = this.querySelector('[data-magnet-count]');
    if (gridLabel) gridLabel.textContent = `${this.gridCols}×${this.gridRows}`;
    if (magnetCount) magnetCount.textContent = this.gridCols * this.gridRows;
  }

  // ========================================
  // MODAL CONTROLS
  // ========================================

  handleTriggerClick() {
    this.modal?.classList.add('active');
    document.body.style.overflow = 'hidden';
    if (this.file) {
      this.showStep('preview');
    } else {
      this.showStep('upload');
    }
    this.querySelector('.puzzle-upload-modal__close')?.focus();
  }

  handleModalClose() {
    this.modal?.classList.remove('active');
    document.body.style.overflow = '';
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
      this.handleModalClose();
    }
  }

  showStep(step) {
    if (step === 'upload') {
      this.uploadStep.style.display = '';
      this.previewStep.style.display = 'none';
    } else {
      this.uploadStep.style.display = 'none';
      this.previewStep.style.display = '';
      this.updatePreview();
    }
  }

  // ========================================
  // FILE HANDLING
  // ========================================

  handleDragOver(e) {
    e.preventDefault();
    this.dropzone?.classList.add('drag-over');
  }

  handleDragLeave(e) {
    e.preventDefault();
    this.dropzone?.classList.remove('drag-over');
  }

  handleDrop(e) {
    e.preventDefault();
    this.dropzone?.classList.remove('drag-over');
    const files = Array.from(e.dataTransfer?.files || []);
    this.processFiles(files);
  }

  handleFileSelect(e) {
    const files = Array.from(e.target?.files || []);
    this.processFiles(files);
    if (this.fileInput) this.fileInput.value = '';
  }

  async processFiles(files) {
    const file = files.find(f => this.allowedTypes.includes(f.type));
    if (!file) return;

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > this.maxFileSizeMB) {
      alert(`File too large (${sizeMB.toFixed(1)}MB). Maximum is ${this.maxFileSizeMB}MB.`);
      return;
    }

    // Read original at full quality
    const originalDataUrl = await this.readFileAsDataUrl(file);
    // Crop to current ratio
    const croppedDataUrl = await this.cropByRatio(originalDataUrl, this.cropRatio);

    this.file = {
      file,
      dataUrl: croppedDataUrl,
      originalDataUrl,
      id: 'puzzle_' + Math.random().toString(36).substr(2, 9)
    };

    this.showStep('preview');
    this.updateTriggerButton();
    this.updateHiddenInputs();
  }

  readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Center-crop image to target aspect ratio and resize.
   * @param {string} dataUrl — source image
   * @param {number} ratio — width / height (e.g. 4/3 = 1.333)
   * @returns {Promise<string>} cropped data URL
   */
  cropByRatio(dataUrl, ratio) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(this.autoCropByRatio(img, ratio, 1200, 0.85));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  }

  /**
   * Auto-crop an image to target ratio (center crop) and resize.
   */
  autoCropByRatio(img, ratio, maxSize, quality) {
    const w = img.width;
    const h = img.height;

    let cropW, cropH;
    if (w / h > ratio) {
      // Image is wider — crop width
      cropH = h;
      cropW = Math.round(h * ratio);
    } else {
      // Image is taller — crop height
      cropW = w;
      cropH = Math.round(w / ratio);
    }

    const sx = Math.round((w - cropW) / 2);
    const sy = Math.round((h - cropH) / 2);

    // Output size: fit within maxSize
    let outW = cropW;
    let outH = cropH;
    if (outW > maxSize || outH > maxSize) {
      const scale = Math.min(maxSize / outW, maxSize / outH);
      outW = Math.round(outW * scale);
      outH = Math.round(outH * scale);
    }

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, outW, outH);
    return canvas.toDataURL('image/jpeg', quality);
  }

  /**
   * Re-crop from original when variant ratio changes.
   */
  async reCropForNewRatio() {
    if (!this.file?.originalDataUrl) return;
    this.file.dataUrl = await this.cropByRatio(this.file.originalDataUrl, this.cropRatio);
    this.updatePreview();
    this.updateHiddenInputs();
  }

  handleChangePicture() {
    this.fileInput?.click();
  }

  // ========================================
  // ROTATE (bake into pixels immediately)
  // ========================================

  async handleRotate() {
    if (!this.file?.originalDataUrl) return;

    this.rotation = (this.rotation + 90) % 360;

    // Rotate the original image by cumulative degrees
    const rotatedDataUrl = await this.rotateImage(this.file.originalDataUrl, this.rotation);

    // Swap grid cols/rows for rotated orientation
    const isOddRotation = this.rotation === 90 || this.rotation === 270;
    this.gridCols = isOddRotation ? this.baseGridRows : this.baseGridCols;
    this.gridRows = isOddRotation ? this.baseGridCols : this.baseGridRows;
    this.cropRatio = this.gridCols / this.gridRows;

    // Re-crop the rotated image to the new ratio
    this.file.dataUrl = await this.cropByRatio(rotatedDataUrl, this.cropRatio);

    this.updatePreview();
    this.updateVariantDisplay();
    this.updateHiddenInputs();
  }

  /**
   * Rotate an image by given degrees (0, 90, 180, 270).
   * Bakes rotation into actual pixels.
   */
  rotateImage(dataUrl, degrees) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const isRotated = degrees === 90 || degrees === 270;
        const canvas = document.createElement('canvas');
        canvas.width = isRotated ? img.height : img.width;
        canvas.height = isRotated ? img.width : img.height;
        const ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  }

  // ========================================
  // PREVIEW & GRID OVERLAY
  // ========================================

  updatePreview() {
    if (!this.imageContainer || !this.file) return;
    this.updateVariantDisplay();

    this.imageContainer.innerHTML = `
      <div class="puzzle-preview__wrapper" style="aspect-ratio: ${this.gridCols} / ${this.gridRows};">
        <img class="puzzle-preview__image" src="${this.file.dataUrl}" alt="Puzzle preview" />
        <div class="puzzle-grid-overlay" style="grid-template-columns: repeat(${this.gridCols}, 1fr); grid-template-rows: repeat(${this.gridRows}, 1fr);">
          ${this.renderGridCells()}
        </div>
      </div>
    `;
  }

  renderGridCells() {
    const total = this.gridCols * this.gridRows;
    let html = '';
    for (let i = 0; i < total; i++) {
      html += `<div class="puzzle-grid-overlay__cell"><span class="puzzle-grid-overlay__number">${i + 1}</span></div>`;
    }
    return html;
  }

  // ========================================
  // RESIZE (CROP) MODAL
  // ========================================

  openResizeModal() {
    if (!this.file) return;

    const cropModal = document.createElement('div');
    cropModal.className = 'puzzle-upload-crop-modal';
    cropModal.innerHTML = `
      <div class="puzzle-upload-crop-modal__overlay"></div>
      <div class="puzzle-upload-crop-modal__container">
        <div class="puzzle-upload-crop-modal__header">
          <h3>Resize Image</h3>
          <button type="button" class="puzzle-upload-crop-modal__close">&times;</button>
        </div>
        <div class="puzzle-upload-crop-modal__body">
          <div class="puzzle-upload-crop-modal__canvas-wrapper">
            <canvas class="puzzle-upload-crop-modal__canvas"></canvas>
            <div class="puzzle-upload-crop-modal__crop-area">
              <div class="puzzle-upload-crop-modal__resize-handle puzzle-upload-crop-modal__resize-handle--nw" data-handle="nw"></div>
              <div class="puzzle-upload-crop-modal__resize-handle puzzle-upload-crop-modal__resize-handle--ne" data-handle="ne"></div>
              <div class="puzzle-upload-crop-modal__resize-handle puzzle-upload-crop-modal__resize-handle--sw" data-handle="sw"></div>
              <div class="puzzle-upload-crop-modal__resize-handle puzzle-upload-crop-modal__resize-handle--se" data-handle="se"></div>
            </div>
          </div>
          <div class="puzzle-upload-crop-modal__toolbar">
            <button type="button" class="puzzle-upload-crop-modal__tool-btn" data-tool="flip-h" title="Flip Horizontal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M16 3l4 4-4 4"/><path d="M20 7H4"/>
                <path d="M8 21l-4-4 4-4"/><path d="M4 17h16"/>
              </svg>
              <span>Flip H</span>
            </button>
            <button type="button" class="puzzle-upload-crop-modal__tool-btn" data-tool="flip-v" title="Flip Vertical">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M3 8l4-4 4 4"/><path d="M7 4v16"/>
                <path d="M21 16l-4 4-4-4"/><path d="M17 20V4"/>
              </svg>
              <span>Flip V</span>
            </button>
            <button type="button" class="puzzle-upload-crop-modal__tool-btn" data-tool="rotate-cw" title="Rotate 90°">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              <span>Rotate</span>
            </button>
          </div>
        </div>
        <div class="puzzle-upload-crop-modal__footer">
          <button type="button" class="puzzle-upload-crop-modal__cancel">Cancel</button>
          <button type="button" class="puzzle-upload-crop-modal__apply">Apply</button>
        </div>
      </div>
    `;

    document.body.appendChild(cropModal);

    let flipH = false;
    let flipV = false;
    let rotation = 0;
    const ratio = this.cropRatio;

    // Load from original file for best quality
    const img = new Image();
    let objectUrl = null;
    if (this.file.file) {
      objectUrl = URL.createObjectURL(this.file.file);
    }

    img.onload = () => {
      const canvas = cropModal.querySelector('.puzzle-upload-crop-modal__canvas');
      const ctx = canvas.getContext('2d');
      const wrapper = cropModal.querySelector('.puzzle-upload-crop-modal__canvas-wrapper');
      const cropArea = cropModal.querySelector('.puzzle-upload-crop-modal__crop-area');

      const isMobile = window.innerWidth <= 767;
      const maxSize = isMobile ? 400 : 600;

      const getDisplayDims = () => {
        const isRotated = rotation === 90 || rotation === 270;
        const srcW = isRotated ? img.height : img.width;
        const srcH = isRotated ? img.width : img.height;
        let dw = srcW, dh = srcH;
        if (dw > maxSize || dh > maxSize) {
          const r = Math.min(maxSize / dw, maxSize / dh);
          dw = Math.round(srcW * r);
          dh = Math.round(srcH * r);
        }
        return { displayWidth: dw, displayHeight: dh };
      };

      const redrawCanvas = () => {
        const { displayWidth, displayHeight } = getDisplayDims();
        canvas.width = displayWidth;
        canvas.height = displayHeight;
        wrapper.style.width = displayWidth + 'px';
        wrapper.style.height = displayHeight + 'px';

        ctx.save();
        ctx.clearRect(0, 0, displayWidth, displayHeight);
        ctx.translate(displayWidth / 2, displayHeight / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

        const isRotated = rotation === 90 || rotation === 270;
        const drawW = isRotated ? displayHeight : displayWidth;
        const drawH = isRotated ? displayWidth : displayHeight;
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        ctx.restore();

        // Reset crop area to ratio-constrained
        initCropArea(displayWidth, displayHeight);
      };

      // Initialize crop area constrained to ratio
      let cropX, cropY, cropW, cropH;
      const minCropDim = 50;

      const initCropArea = (dw, dh) => {
        if (dw / dh > ratio) {
          cropH = dh;
          cropW = Math.round(dh * ratio);
        } else {
          cropW = dw;
          cropH = Math.round(dw / ratio);
        }
        cropX = Math.round((dw - cropW) / 2);
        cropY = Math.round((dh - cropH) / 2);
        updateCropAreaUI();
      };

      const updateCropAreaUI = () => {
        cropArea.style.width = cropW + 'px';
        cropArea.style.height = cropH + 'px';
        cropArea.style.left = cropX + 'px';
        cropArea.style.top = cropY + 'px';
      };

      // Initial draw
      let { displayWidth, displayHeight } = getDisplayDims();
      canvas.width = displayWidth;
      canvas.height = displayHeight;
      wrapper.style.width = displayWidth + 'px';
      wrapper.style.height = displayHeight + 'px';
      ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
      initCropArea(displayWidth, displayHeight);

      // Toolbar events
      cropModal.querySelector('[data-tool="flip-h"]').addEventListener('click', () => { flipH = !flipH; redrawCanvas(); });
      cropModal.querySelector('[data-tool="flip-v"]').addEventListener('click', () => { flipV = !flipV; redrawCanvas(); });
      cropModal.querySelector('[data-tool="rotate-cw"]').addEventListener('click', () => {
        rotation = (rotation + 90) % 360;
        redrawCanvas();
        const dims = getDisplayDims();
        displayWidth = dims.displayWidth;
        displayHeight = dims.displayHeight;
      });

      // Drag and resize state
      let isDragging = false;
      let isResizing = false;
      let resizeHandle = null;
      let startX = 0, startY = 0, startCropX = 0, startCropY = 0, startCropW = 0, startCropH = 0;

      const handleCropAreaDown = (e) => {
        const point = e.touches ? e.touches[0] : e;
        if (e.target.dataset.handle) return;
        isDragging = true;
        startX = point.clientX;
        startY = point.clientY;
        startCropX = cropX;
        startCropY = cropY;
        e.preventDefault();
      };

      cropArea.addEventListener('mousedown', handleCropAreaDown);
      cropArea.addEventListener('touchstart', handleCropAreaDown, { passive: false });

      // Resize handles
      cropModal.querySelectorAll('.puzzle-upload-crop-modal__resize-handle').forEach(handle => {
        const handleDown = (e) => {
          const point = e.touches ? e.touches[0] : e;
          isResizing = true;
          resizeHandle = (e.target || e.currentTarget).dataset.handle;
          startX = point.clientX;
          startY = point.clientY;
          startCropX = cropX;
          startCropY = cropY;
          startCropW = cropW;
          startCropH = cropH;
          e.preventDefault();
          e.stopPropagation();
        };
        handle.addEventListener('mousedown', handleDown);
        handle.addEventListener('touchstart', handleDown, { passive: false });
      });

      const handlePointerMove = (e) => {
        const point = e.touches ? e.touches[0] : e;
        const curDims = getDisplayDims();
        const dw = curDims.displayWidth;
        const dh = curDims.displayHeight;

        if (isDragging) {
          const dx = point.clientX - startX;
          const dy = point.clientY - startY;
          cropX = Math.max(0, Math.min(dw - cropW, startCropX + dx));
          cropY = Math.max(0, Math.min(dh - cropH, startCropY + dy));
          updateCropAreaUI();
        } else if (isResizing) {
          const dx = point.clientX - startX;
          const dy = point.clientY - startY;
          // Use larger delta, maintain ratio
          const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;
          let newW, newH, newX, newY;

          switch (resizeHandle) {
            case 'se':
              newW = Math.max(minCropDim, startCropW + delta);
              newH = Math.round(newW / ratio);
              newX = startCropX;
              newY = startCropY;
              break;
            case 'sw':
              newW = Math.max(minCropDim, startCropW - dx);
              newH = Math.round(newW / ratio);
              newX = startCropX + startCropW - newW;
              newY = startCropY;
              break;
            case 'ne':
              newW = Math.max(minCropDim, startCropW + dx);
              newH = Math.round(newW / ratio);
              newX = startCropX;
              newY = startCropY + startCropH - newH;
              break;
            case 'nw':
              newW = Math.max(minCropDim, startCropW - delta);
              newH = Math.round(newW / ratio);
              newX = startCropX + startCropW - newW;
              newY = startCropY + startCropH - newH;
              break;
            default: return;
          }

          // Validate bounds
          if (newX >= 0 && newY >= 0 && newX + newW <= dw && newY + newH <= dh && newW >= minCropDim && newH >= minCropDim) {
            cropW = newW;
            cropH = newH;
            cropX = newX;
            cropY = newY;
            updateCropAreaUI();
          }
        }
      };

      const handlePointerUp = () => {
        isDragging = false;
        isResizing = false;
        resizeHandle = null;
      };

      document.addEventListener('mousemove', handlePointerMove);
      document.addEventListener('mouseup', handlePointerUp);
      document.addEventListener('touchmove', handlePointerMove, { passive: false });
      document.addEventListener('touchend', handlePointerUp);

      // Apply crop + transforms
      cropModal.querySelector('.puzzle-upload-crop-modal__apply').addEventListener('click', () => {
        const curDims = getDisplayDims();
        const dw = curDims.displayWidth;
        const dh = curDims.displayHeight;

        // Full-size transformed canvas
        const isRotated = rotation === 90 || rotation === 270;
        const fullW = isRotated ? img.height : img.width;
        const fullH = isRotated ? img.width : img.height;

        const transformCanvas = document.createElement('canvas');
        transformCanvas.width = fullW;
        transformCanvas.height = fullH;
        const tCtx = transformCanvas.getContext('2d');

        tCtx.translate(fullW / 2, fullH / 2);
        tCtx.rotate((rotation * Math.PI) / 180);
        tCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

        const drawW = isRotated ? fullH : fullW;
        const drawH = isRotated ? fullW : fullH;
        tCtx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

        // Crop from transformed canvas
        const scaleX = fullW / dw;
        const scaleY = fullH / dh;
        const actualX = Math.round(cropX * scaleX);
        const actualY = Math.round(cropY * scaleY);
        const actualW = Math.round(cropW * scaleX);
        const actualH = Math.round(cropH * scaleY);

        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = actualW;
        cropCanvas.height = actualH;
        const cropCtx = cropCanvas.getContext('2d');
        cropCtx.drawImage(transformCanvas, actualX, actualY, actualW, actualH, 0, 0, actualW, actualH);

        // Compress and save
        const tempImg = new Image();
        tempImg.onload = () => {
          this.file.dataUrl = this.compressImage(tempImg, 1200, 0.85);
          // Also update originalDataUrl with the transformed version
          // so future ratio changes re-crop from the user's latest edits
          this.file.originalDataUrl = this.file.dataUrl;
          cleanup();
          this.updatePreview();
          this.updateHiddenInputs();
        };
        tempImg.src = cropCanvas.toDataURL('image/jpeg', 0.9);
      });

      // Cleanup
      const cleanup = () => {
        document.removeEventListener('mousemove', handlePointerMove);
        document.removeEventListener('mouseup', handlePointerUp);
        document.removeEventListener('touchmove', handlePointerMove);
        document.removeEventListener('touchend', handlePointerUp);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        cropModal.remove();
      };

      cropModal.querySelector('.puzzle-upload-crop-modal__close').addEventListener('click', cleanup);
      cropModal.querySelector('.puzzle-upload-crop-modal__cancel').addEventListener('click', cleanup);
      cropModal.querySelector('.puzzle-upload-crop-modal__overlay').addEventListener('click', cleanup);
    };

    img.src = objectUrl || this.file.originalDataUrl || this.file.dataUrl;
  }

  // ========================================
  // IMAGE UTILITIES
  // ========================================

  compressImage(img, maxSize, quality) {
    let width = img.width;
    let height = img.height;
    if (width > maxSize || height > maxSize) {
      const r = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * r);
      height = Math.round(height * r);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', quality);
  }

  compressForCart(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(this.compressImage(img, 400, 0.6));
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  }

  compressForOrderThumbnail(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(this.compressImage(img, 400, 0.6));
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  }

  /**
   * Render puzzle preview with grid overlay onto a canvas and return as data URL.
   * @param {number} [size=400] - Max dimension for the output
   * @param {number} [quality=0.7] - JPEG quality (0-1)
   */
  renderPuzzlePreviewImage(size, quality) {
    size = size || 400;
    quality = quality || 0.7;
    return new Promise((resolve) => {
      if (!this.file) { resolve(null); return; }
      const img = new Image();
      img.onload = () => {
        const w = Math.round(size);
        const h = Math.round(size / this.cropRatio);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        // Draw image
        ctx.drawImage(img, 0, 0, w, h);

        // Draw grid lines
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = size > 600 ? 3 : 2;
        ctx.setLineDash([6, 4]);

        // Vertical lines
        for (let i = 1; i < this.gridCols; i++) {
          const x = Math.round((i / this.gridCols) * w);
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, h);
          ctx.stroke();
        }
        // Horizontal lines
        for (let i = 1; i < this.gridRows; i++) {
          const y = Math.round((i / this.gridRows) * h);
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(w, y);
          ctx.stroke();
        }

        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => resolve(null);
      img.src = this.file.dataUrl;
    });
  }

  // ========================================
  // CLOUDFLARE WORKER
  // ========================================

  getWorkerConfig() {
    const url = this.dataset.workerUrl?.trim();
    const key = this.dataset.workerKey?.trim();
    if (!url) return null;
    return { url: url.replace(/\/$/, ''), key: key || '' };
  }

  dataUrlToBlob(dataUrl) {
    const [header, base64Data] = dataUrl.split(',');
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const binary = atob(base64Data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  }

  async uploadToWorker(fileOrBlob, filename, config) {
    try {
      const formData = new FormData();
      formData.append('file', fileOrBlob, filename);
      const response = await fetch(`${config.url}/upload`, {
        method: 'POST',
        headers: { 'X-API-Key': config.key },
        body: formData
      });
      if (!response.ok) return null;
      const result = await response.json();
      return result.url || null;
    } catch (err) {
      console.warn('[PuzzleUpload] Worker upload failed:', err.message);
      return null;
    }
  }

  // ========================================
  // HIDDEN INPUTS & FORM SYNC
  // ========================================

  updateHiddenInputs() {
    const container = this.querySelector('.puzzle-upload-hidden-inputs');
    if (!container) return;

    let html = '';
    if (this.file) {
      html += `<input type="hidden" name="properties[_puzzle_grid]" value="${this.gridCols}x${this.gridRows}" />`;
      html += `<input type="hidden" name="properties[_puzzle_count]" value="${this.gridCols * this.gridRows}" />`;
      html += `<input type="hidden" name="properties[_upload_id]" value="${Date.now().toString()}" />`;
    }
    container.innerHTML = html;
    this.syncInputsToProductForm();
  }

  syncInputsToProductForm() {
    const productFormComponent = this.closest('product-form-component') ||
      document.querySelector('product-form-component');
    const productForm = productFormComponent?.querySelector('form') ||
      document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return;

    productForm.querySelectorAll('input[data-puzzle-upload="true"]').forEach(el => el.remove());

    const hiddenInputs = this.querySelectorAll('.puzzle-upload-hidden-inputs input');
    hiddenInputs.forEach(input => {
      const clone = input.cloneNode(true);
      clone.style.display = 'none';
      clone.setAttribute('data-puzzle-upload', 'true');
      productForm.appendChild(clone);
    });
  }

  updateTriggerButton() {
    if (!this.trigger) return;
    if (this.file) {
      this.trigger.classList.add('puzzle-upload-trigger--filled');
      this.trigger.querySelector('span').textContent = '1 photo selected';
    } else {
      this.trigger.classList.remove('puzzle-upload-trigger--filled');
      this.trigger.querySelector('span').textContent = this.dataset.buttonText || 'Upload your photo';
    }
  }

  // ========================================
  // ADD TO CART
  // ========================================

  async handleAddToCart() {
    if (!this.file) {
      alert('Please upload a photo first');
      return;
    }
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const addToCartBtn = this.querySelector('.puzzle-upload-addtocart-btn');
    if (addToCartBtn) {
      addToCartBtn.textContent = 'ADDING...';
      addToCartBtn.disabled = true;
    }

    try {
      // Get variant ID
      let variantId = this.getVariantFromPicker();
      if (!variantId) {
        const urlParams = new URLSearchParams(window.location.search);
        variantId = urlParams.get('variant');
      }
      if (!variantId) variantId = this.dataset.variantId;
      if (!variantId) throw new Error('Could not determine product variant');

      const properties = {
        '_puzzle_grid': `${this.gridCols}x${this.gridRows}`,
        '_puzzle_count': (this.gridCols * this.gridRows).toString(),
        '_upload_id': Date.now().toString()
      };

      const workerConfig = this.getWorkerConfig();
      if (!workerConfig) throw new Error('Image upload service is not configured. Please contact support.');

      // === UPLOAD 1: Original uncropped photo to R2 ===
      if (addToCartBtn) addToCartBtn.textContent = 'UPLOADING ORIGINAL...';
      const originalFilename = this.file.file?.name || 'puzzle-original.jpg';

      let originalUrl = null;
      if (this.file.file) {
        originalUrl = await this.uploadToWorker(this.file.file, originalFilename, workerConfig);
      } else if (this.file.originalDataUrl) {
        const originalBlob = this.dataUrlToBlob(this.file.originalDataUrl);
        originalUrl = await this.uploadToWorker(originalBlob, originalFilename, workerConfig);
      }
      if (!originalUrl) throw new Error('Failed to upload original photo. Please try again.');
      properties['Original Photo'] = originalUrl;

      // === UPLOAD 2: Cropped puzzle image (ratio-cropped) to R2 ===
      if (addToCartBtn) addToCartBtn.textContent = 'UPLOADING CROPPED...';
      const croppedBlob = this.dataUrlToBlob(this.file.dataUrl);
      const croppedFilename = originalFilename.replace(/\.[^.]+$/, '') + '-cropped.jpg';
      const croppedUrl = await this.uploadToWorker(croppedBlob, croppedFilename, workerConfig);
      if (!croppedUrl) throw new Error('Failed to upload cropped photo. Please try again.');
      properties['Puzzle Image'] = croppedUrl;

      // === UPLOAD 3: Grid overlay composite to R2 ===
      if (addToCartBtn) addToCartBtn.textContent = 'CREATING PREVIEW...';
      const previewDataUrl = await this.renderPuzzlePreviewImage(1200, 0.9);
      if (previewDataUrl) {
        const previewBlob = this.dataUrlToBlob(previewDataUrl);
        const previewFilename = originalFilename.replace(/\.[^.]+$/, '') + '-grid-preview.jpg';
        const previewUrl = await this.uploadToWorker(previewBlob, previewFilename, workerConfig);
        if (!previewUrl) throw new Error('Failed to upload preview. Please try again.');
        properties['Puzzle Preview'] = previewUrl;
      }

      if (addToCartBtn) addToCartBtn.textContent = 'ADDING TO CART...';

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1,
          properties
        })
      });

      const responseText = await response.text();
      let result;
      try { result = JSON.parse(responseText); }
      catch { throw new Error(response.ok ? 'Unexpected server response' : `Server error (${response.status})`); }

      if (result.status && result.status >= 400) throw new Error(result.description || result.message || 'Failed to add to cart');
      if (!response.ok) throw new Error(result.description || result.message || 'Failed to add to cart');

      // Success
      this.handleModalClose();
      const magnetCount = this.gridCols * this.gridRows;

      // Clear
      this.file = null;
      this.updateHiddenInputs();
      this.updateTriggerButton();

      // Update cart
      this.updateCartBubble();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: result }));

      // Success popup
      this.showSuccessPopup(magnetCount);

    } catch (error) {
      console.error('[PuzzleUpload] Error adding to cart:', error);
      alert(error.message || 'Failed to add to cart. Please try again.');
    } finally {
      this.isSubmitting = false;
      if (addToCartBtn) {
        addToCartBtn.textContent = 'ADD TO CART';
        addToCartBtn.disabled = false;
      }
    }
  }

  async updateCartBubble() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartBubbles = document.querySelectorAll(
        '.cart-bubble-count, [data-cart-count], .cart-count, .cart-item-count, cart-bubble-component'
      );
      cartBubbles.forEach(el => {
        if (el.tagName.toLowerCase() === 'cart-bubble-component') {
          el.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        } else {
          el.textContent = cart.item_count;
        }
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (e) {
      console.warn('[PuzzleUpload] Could not update cart bubble:', e);
    }
  }

  async showSuccessPopup(magnetCount = 9) {
    document.querySelectorAll('.puzzle-upload-success').forEach(el => el.remove());

    let cartItemCount = 1;
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      cartItemCount = cart.item_count || 1;
    } catch (e) { /* ignore */ }

    const popup = document.createElement('div');
    popup.className = 'puzzle-upload-success photo-upload-success';
    popup.innerHTML = `
      <button type="button" class="photo-upload-success__close">&times;</button>
      <p class="photo-upload-success__message">
        Your puzzle photo (${magnetCount} magnets) has been added to cart!
      </p>
      <div class="photo-upload-success__buttons">
        <a href="/cart" class="photo-upload-success__btn photo-upload-success__btn--secondary">
          View your cart (${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'})
        </a>
        <a href="/checkout" class="photo-upload-success__btn photo-upload-success__btn--primary">
          SECURE CHECKOUT
        </a>
      </div>
      <a href="#" class="photo-upload-success__more-link" onclick="this.closest('.puzzle-upload-success').remove(); return false;">
        GET MORE MAGNETS
      </a>
    `;

    document.body.appendChild(popup);
    popup.querySelector('.photo-upload-success__close')?.addEventListener('click', () => popup.remove());
    setTimeout(() => popup.remove(), 10000);
  }

  generateId() {
    return 'puzzle_' + Math.random().toString(36).substr(2, 9);
  }
}

// Register custom element
if (!customElements.get('puzzle-upload-component')) {
  customElements.define('puzzle-upload-component', PuzzleUploadComponent);
}
